import { PROVIDER_ACCOUNT_NAMES, type ProviderKind } from "@cmmd-forge/contracts";
import { CheckIcon, CopyIcon, ExternalLinkIcon, LoaderIcon, PlusIcon } from "lucide-react";
import { useState } from "react";

import type {
  CmmdClaudeCredentialState,
  CmmdCodexCredentialState,
} from "../../environments/cmmdApi";
import { openExternalAuthUrl } from "../../hooks/useProviderAuth";
import type { CloudProviderSignInController } from "../../hooks/useCloudProviderSignIn";
import { Button } from "../ui/button";
import { PasteCodeForm } from "./PasteCodeForm";

type CredentialState = CmmdCodexCredentialState | CmmdClaudeCredentialState;

export function ActiveSignInPhase({
  phase,
  provider,
  onStart,
  onCancel,
}: {
  readonly phase: Extract<
    NonNullable<CloudProviderSignInController["phase"]>,
    { kind: "awaiting_user" | "error" }
  >;
  readonly provider: ProviderKind;
  readonly onStart: (provider: ProviderKind) => void;
  readonly onCancel: () => void;
}) {
  if (phase.kind === "awaiting_user") {
    return (
      <AwaitingUserView
        deviceUrl={phase.deviceUrl}
        userCode={phase.userCode}
        expectsPastedCode={phase.expectsPastedCode}
        submitCode={phase.submitCode}
        submitError={phase.submitError}
        onCancel={onCancel}
      />
    );
  }
  return (
    <div className="mt-3 space-y-2">
      <div className="text-destructive">{phase.message}</div>
      <Button className="w-full" onClick={() => onStart(provider)}>
        Try again
      </Button>
    </div>
  );
}

export function CredentialNeedsReconnectView({
  provider,
  disconnecting,
  onReconnect,
  onDisconnect,
}: {
  readonly provider: ProviderKind;
  readonly disconnecting: boolean;
  readonly onReconnect: () => void;
  readonly onDisconnect: () => Promise<void> | void;
}) {
  const accountName = PROVIDER_ACCOUNT_NAMES[provider];
  const [disconnectError, setDisconnectError] = useState<string | null>(null);
  return (
    <div className="mt-3 space-y-3">
      <p className="leading-5 text-destructive">
        Your saved {accountName} sign-in expired. Reconnect to keep using this subscription.
      </p>
      <Button className="w-full" onClick={onReconnect}>
        Reconnect {accountName}
      </Button>
      <Button
        variant="ghost"
        className="w-full"
        disabled={disconnecting}
        onClick={() => {
          setDisconnectError(null);
          void Promise.resolve(onDisconnect()).catch((error: unknown) => {
            setDisconnectError(
              error instanceof Error ? error.message : "Could not disconnect this subscription.",
            );
          });
        }}
      >
        {disconnecting ? <LoaderIcon className="animate-spin" /> : null}
        {disconnecting ? "Disconnecting…" : "Disconnect"}
      </Button>
      {disconnectError ? <p className="leading-5 text-destructive">{disconnectError}</p> : null}
    </div>
  );
}

export function SettledProviderSignInView({
  controller,
  provider,
  showConnected,
  connectedMode,
}: {
  readonly controller: CloudProviderSignInController<CredentialState>;
  readonly provider: ProviderKind;
  readonly showConnected: boolean;
  readonly connectedMode: "manage" | "standalone";
}) {
  const { phase, connection, connectionLoading, disconnecting, start, disconnect } = controller;

  if (showConnected && connection?.connected) {
    if (connectedMode === "standalone") {
      return (
        <CredentialConnectedView
          state={connection}
          disconnecting={disconnecting}
          onDisconnect={disconnect}
        />
      );
    }
    return <ConnectedAddAccountView state={connection} provider={provider} onAdd={start} />;
  }
  if (phase?.kind === "done") {
    return (
      <div className="mt-3 flex items-center gap-2 text-foreground">
        <CheckIcon className="text-success-foreground" />
        Connected. Your subscription is now available across your Forge surfaces.
      </div>
    );
  }
  if (connectionLoading) {
    return <StatusLine label="Checking your subscription…" />;
  }
  return (
    <Button className="mt-3 w-full" onClick={() => start(provider)}>
      Connect your subscription
    </Button>
  );
}

function CredentialConnectedView({
  state,
  disconnecting,
  onDisconnect,
}: {
  readonly state:
    | Extract<CmmdCodexCredentialState, { connected: true }>
    | Extract<CmmdClaudeCredentialState, { connected: true }>;
  readonly disconnecting: boolean;
  readonly onDisconnect: () => Promise<void> | void;
}) {
  const [disconnectError, setDisconnectError] = useState<string | null>(null);
  return (
    <div className="mt-3 space-y-3">
      <div className="flex items-center gap-2 text-foreground">
        <CheckIcon className="text-success-foreground" />
        Connected{state.accountEmail ? ` as ${state.accountEmail}` : ""}.
      </div>
      <Button
        variant="ghost"
        className="w-full"
        disabled={disconnecting}
        onClick={() => {
          setDisconnectError(null);
          void Promise.resolve(onDisconnect()).catch((error: unknown) => {
            setDisconnectError(
              error instanceof Error ? error.message : "Could not disconnect this subscription.",
            );
          });
        }}
      >
        {disconnecting ? <LoaderIcon className="animate-spin" /> : null}
        {disconnecting ? "Disconnecting…" : "Disconnect"}
      </Button>
      {disconnectError ? <p className="leading-5 text-destructive">{disconnectError}</p> : null}
    </div>
  );
}

export function StatusLine({ label }: { readonly label: string }) {
  return (
    <div className="mt-3 flex items-center gap-2 text-muted-foreground">
      <LoaderIcon className="animate-spin" />
      {label}
    </div>
  );
}

function ConnectedAddAccountView({
  state,
  provider,
  onAdd,
}: {
  readonly state:
    | Extract<CmmdCodexCredentialState, { connected: true }>
    | Extract<CmmdClaudeCredentialState, { connected: true }>;
  readonly provider: ProviderKind;
  readonly onAdd: (provider: ProviderKind) => void;
}) {
  return (
    <div className="mt-3 flex items-start justify-between gap-3 rounded-md border border-border/70 bg-background/40 p-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2 text-foreground">
          <CheckIcon className="size-4 text-success-foreground" />
          <span className="font-medium">Subscription connected</span>
        </div>
        <div className="mt-1 truncate text-muted-foreground">
          {state.accountEmail ? state.accountEmail : "Account connected"}
          {state.planType ? <span> · {state.planType}</span> : null}
        </div>
      </div>
      <Button size="sm" variant="outline" className="shrink-0" onClick={() => onAdd(provider)}>
        <PlusIcon />
        Add account
      </Button>
    </div>
  );
}

function awaitingInstruction(input: {
  readonly expectsPastedCode: boolean;
  readonly locked: boolean;
}): string {
  if (!input.expectsPastedCode) return "Open the sign-in page and enter the code to finish.";
  if (input.locked) return "Checking your code…";
  return "Open the sign-in page, then paste the code it gives you back here.";
}

function AwaitingUserView({
  deviceUrl,
  userCode,
  expectsPastedCode,
  submitCode,
  submitError,
  onCancel,
}: {
  readonly deviceUrl: string | null;
  readonly userCode: string | null;
  readonly expectsPastedCode: boolean;
  readonly submitCode: ((code: string) => Promise<void>) | null;
  readonly submitError: string | null;
  readonly onCancel: () => void;
}) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="mt-3 space-y-2">
      <div className="text-muted-foreground">
        {awaitingInstruction({ expectsPastedCode, locked: submitCode === null })}
      </div>
      {userCode ? (
        <div className="flex items-center justify-between gap-2 rounded border border-border/70 bg-background px-2 py-1.5">
          <code className="font-mono text-sm tracking-wider text-foreground">{userCode}</code>
          <Button
            variant="outline"
            size="sm"
            className="h-7 px-2"
            onClick={() => {
              void navigator.clipboard?.writeText(userCode).then(
                () => setCopied(true),
                () => undefined,
              );
            }}
          >
            {copied ? <CheckIcon /> : <CopyIcon />}
            {copied ? "Copied" : "Copy"}
          </Button>
        </div>
      ) : null}
      {deviceUrl ? (
        <Button className="w-full" onClick={() => openExternalAuthUrl(deviceUrl)}>
          <ExternalLinkIcon />
          Open sign-in page
        </Button>
      ) : null}
      {submitCode ? <PasteCodeForm submitCode={submitCode} /> : null}
      {submitError ? (
        <p className="text-xs leading-5 text-destructive" role="alert">
          {submitError}
        </p>
      ) : null}
      <Button variant="ghost" size="sm" className="w-full" onClick={onCancel}>
        Cancel
      </Button>
    </div>
  );
}
